export type FrameVariant = 'page' | 'hero' | 'game-card';

export interface FrameAssetSet {
  corners: {
    topLeft: string;
    topRight: string;
    bottomLeft: string;
    bottomRight: string;
  };
  edges: {
    horizontal: string;
    vertical: string;
  };
  /** Intended rendered corner size in CSS pixels. */
  cornerSize: number;
  /** Intended rendered edge thickness in CSS pixels. */
  edgeThickness: number;
  /** How far the artwork may sit outside the panel boundary. */
  overhang: number;
}

/**
 * Every asset reference below is written out as a literal
 * `new URL('./relative/path.png', import.meta.url).href`, and deliberately not
 * routed through a helper.
 *
 * Vite only rewrites `new URL(..., import.meta.url)` when the first argument
 * is a string literal *at the call site*: that is what makes it emit the file
 * into `dist/assets/` and substitute the hashed URL. Passing the path through
 * a helper defeats that analysis — the relative string survives into the
 * bundle and is resolved at runtime against the emitted chunk's own URL
 * (`/assets/components-<hash>.js`), so every frame 404s in a production build
 * while still loading in dev, where the chunk *is* the source module.
 *
 * Keep these calls literal and inline.
 */

export const frameAssetManifest = {
  page: {
    corners: {
      topLeft: new URL('./ui/frames/page/page-corner-tl.png', import.meta.url).href,
      topRight: new URL('./ui/frames/page/page-corner-tr.png', import.meta.url).href,
      bottomLeft: new URL('./ui/frames/page/page-corner-bl.png', import.meta.url).href,
      bottomRight: new URL('./ui/frames/page/page-corner-br.png', import.meta.url).href,
    },
    edges: {
      horizontal: new URL('./ui/frames/page/page-edge-h.png', import.meta.url).href,
      vertical: new URL('./ui/frames/page/page-edge-v.png', import.meta.url).href,
    },
    cornerSize: 40,
    edgeThickness: 3,
    // Clipped to the panel: the dog-ear art is drawn inside the boundary
    // rather than hanging past it.
    overhang: 0,
  },
  hero: {
    corners: {
      topLeft: new URL('./ui/frames/hero/hero-corner-tl.png', import.meta.url).href,
      topRight: new URL('./ui/frames/hero/hero-corner-tr.png', import.meta.url).href,
      bottomLeft: new URL('./ui/frames/hero/hero-corner-bl.png', import.meta.url).href,
      bottomRight: new URL('./ui/frames/hero/hero-corner-br.png', import.meta.url).href,
    },
    edges: {
      horizontal: new URL('./ui/frames/hero/hero-edge-h-major.png', import.meta.url).href,
      vertical: new URL('./ui/frames/hero/hero-edge-v.png', import.meta.url).href,
    },
    cornerSize: 42,
    edgeThickness: 6,
    overhang: 0,
  },
  'game-card': {
    corners: {
      topLeft: new URL('./ui/frames/game-card/game-card-corner-tl.png', import.meta.url).href,
      topRight: new URL('./ui/frames/game-card/game-card-corner-tr.png', import.meta.url).href,
      bottomLeft: new URL('./ui/frames/game-card/game-card-corner-bl.png', import.meta.url).href,
      bottomRight: new URL('./ui/frames/game-card/game-card-corner-br.png', import.meta.url).href,
    },
    edges: {
      horizontal: new URL('./ui/frames/game-card/game-card-edge-h.png', import.meta.url).href,
      vertical: new URL('./ui/frames/game-card/game-card-edge-v.png', import.meta.url).href,
    },
    cornerSize: 28,
    edgeThickness: 2,
    overhang: 1,
  },
} as const satisfies Record<FrameVariant, FrameAssetSet>;

export const ornamentAssetManifest = {
  diamondSmall: new URL('./ui/ornaments/diamond-small.png', import.meta.url).href,
  diamondLarge: new URL('./ui/ornaments/diamond-large.png', import.meta.url).href,
  ruleEndLeft: new URL('./ui/ornaments/rule-end-left.png', import.meta.url).href,
  ruleEndRight: new URL('./ui/ornaments/rule-end-right.png', import.meta.url).href,
} as const;
